import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { FileUpIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FONT_ACCEPTED_FILE_TYPES } from '@inference.engine/file-utils/web';
import type { TFunction } from 'i18next';

import {
  specimenFamily,
  toFontSpecimen,
  useFontFaces,
  type BrandFontItem,
} from '@/entities/brand-font';
import type { MediaFolder } from '@/entities/brand-media';
import { usePermissions } from '@/entities/user';
import { formatFileSize } from '@/shared/utils/format';

import {
  Field,
  PanelShell,
  Section,
  formatRecordedDate,
  useResizablePanel,
} from './DetailPanelShell';
import { MoveToFolderMenu } from './MoveToFolderMenu';

/** What the replace picker offers: the font formats an upload accepts, as an `accept` list. */
const FONT_ACCEPT = Object.entries(FONT_ACCEPTED_FILE_TYPES)
  .flatMap(([mimeType, extensions]) => [mimeType, ...extensions])
  .join(',');

const sectionLabels = (t: TFunction<'tools'>): Record<string, string> => ({
  properties: t('brandFont.sections.properties'),
  licence: t('brandFont.sections.licence'),
});

/**
 * What the library knows about one font, beside the grid it sits in.
 *
 * The same panel an asset opens, down to the width you can drag and the pair of controls in the
 * footer, because the two sit in one column and switching between them should not feel like
 * switching surfaces. What it does not carry is a tag editor: fonts are filed in folders and not
 * tagged, so there would be nothing behind the section.
 *
 * Most of what it shows is what the file said about itself at upload. A font added before the
 * uploader started reading those fields shows "Not recorded" against each until the same file is
 * uploaded again, which is the honest reading: absent, not empty.
 */
export const FontDetailPanel = ({
  font,
  url,
  folders,
  onClose,
  onMove,
  onReplace,
  isReplacing = false,
  onRequestDelete,
}: {
  font: BrandFontItem;
  /**
   * The font file, for the specimen at the top.
   *
   * Undefined while the url batch resolves, which the interface font covers: the specimen falls
   * back to it rather than leaving the panel blank, the same way the card does.
   */
  url?: string;
  /** Everywhere this font could be filed, for the footer's move control. */
  folders: readonly MediaFolder[];
  onClose: () => void;
  /** Raised with the chosen destination, `null` to take the font out of every folder. */
  onMove: (folderId: string | null) => void;
  /** Raised with the file a person chose to put behind this font in place of its current one. */
  onReplace: (file: File) => void;
  isReplacing?: boolean;
  /**
   * Raised rather than handled.
   *
   * One dialog and one delete path for the whole library, so the single-versus-several wording and
   * the selection reset afterwards live in one place.
   */
  onRequestDelete: () => void;
}) => {
  const { t } = useTranslation('tools');
  const { canManageBrandAssets } = usePermissions();
  const noPermission = t('brandMediaLibrary.permissions.noPermission');
  const labels = sectionLabels(t);
  const notRecorded = t('brandFont.notRecorded');

  const { width, startResize } = useResizablePanel();
  const replacePicker = useRef<HTMLInputElement>(null);

  // Registered here rather than relying on the grid behind it. The grid registers the same face,
  // and a second `FontFace` on the same family is harmless, but it means this panel still draws
  // the real typeface when it is opened from a surface that has no specimen grid.
  useFontFaces([toFontSpecimen(font, url === undefined ? undefined : { [font.assetId]: url })]);

  const name = font.familyName ?? font.filename;

  return (
    // Its own provider rather than the app's: this panel is shared code, mounted by more than
    // one host, and a tooltip whose provider is missing throws rather than degrading.
    <TooltipProvider>
      <PanelShell width={width} testId="font-detail-panel">
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t('brandFont.resizePanel')}
          onPointerDown={startResize}
          className="absolute inset-y-0 left-0 z-10 w-1 -translate-x-1/2 cursor-col-resize touch-none"
        />

        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border-light px-lg py-md">
          <span className="type-heading-sm truncate text-foreground">
            {t('brandFont.detailPanel')}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="shrink-0"
                aria-label={t('brandFont.close')}
                onClick={onClose}
              >
                <XIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('brandFont.close')}</TooltipContent>
          </Tooltip>
        </div>

        {/* Same as the asset panel: the column scrolls, so nothing in it is compressed to fit. The
            specimen and the truncated filename both carry `overflow: hidden`, which drops their
            content-based minimum, and without this the specimen's 4/3 box squashes the name under
            it until only its top half is drawn. */}
        <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-5 [&>*]:shrink-0">
          {/* Where an asset panel puts the picture, answering the same question: what does this
              actually look like. A pangram rather than the card's "Aa", because the panel has the
              width for it and a typeface is judged on how letters sit together. Hidden from a
              screen reader, which would otherwise read the sentence as if it were content. */}
          {/* Out to the panel's own edges with a rule under it, the way the asset panel shows a
              picture: the specimen is drawn on the panel's white rather than in a box on it, so the
              line under it is what separates the sample from the fields. Same 4/3 box the asset
              panel gives a still, so switching between a font and an image does not move the fields
              under them; the sentence sits centred in it and is clipped rather than allowed to push
              the box taller at the narrowest panel width. */}
          <div
            data-testid="font-detail-specimen"
            aria-hidden="true"
            className="-mx-5 -mt-5 flex aspect-[4/3] items-center overflow-hidden border-b border-border-light p-5 text-3xl leading-snug text-foreground"
            style={{
              fontFamily: `"${specimenFamily(font.id)}", var(--font-sans, sans-serif)`,
              ...(font.weight !== null ? { fontWeight: font.weight } : {}),
              ...(font.style !== null ? { fontStyle: font.style } : {}),
            }}
          >
            {t('brandFont.specimenSentence')}
          </div>

          <p className="type-body-lg-strong truncate text-foreground" title={name}>
            {name}
          </p>

          <Section title={labels.properties}>
            <dl className="flex flex-col gap-2">
              <Field
                label={t('brandFont.properties.family')}
                value={font.familyName ?? notRecorded}
                testId="font-detail-family"
              />
              <Field
                label={t('brandFont.properties.weight')}
                value={font.weight ?? notRecorded}
                testId="font-detail-weight"
              />
              <Field
                label={t('brandFont.properties.style')}
                value={font.style ?? notRecorded}
                testId="font-detail-style"
              />
              <Field
                label={t('brandFont.properties.format')}
                value={font.format?.toUpperCase() ?? notRecorded}
                testId="font-detail-format"
              />
              {/* Zero is what a row recorded before the uploader measured anything carries, and "0 B"
                  would read as a font with no bytes in it rather than as a size nobody took. */}
              <Field
                label={t('brandFont.properties.fileSize')}
                value={font.sizeBytes > 0 ? formatFileSize(font.sizeBytes) : notRecorded}
                testId="font-detail-size"
              />
              <Field
                label={t('brandFont.properties.filename')}
                value={font.filename}
                testId="font-detail-filename"
              />
              <Field
                label={t('brandFont.properties.added')}
                value={formatRecordedDate(font.createdAt, notRecorded)}
                testId="font-detail-added"
              />
            </dl>
          </Section>

          <Section title={labels.licence}>
            <dl className="flex flex-col gap-2">
              <Field
                label={t('brandFont.properties.foundry')}
                value={font.foundry ?? notRecorded}
                testId="font-detail-foundry"
              />
              <Field
                label={t('brandFont.properties.designer')}
                value={font.designer ?? notRecorded}
                testId="font-detail-designer"
              />
              <Field
                label={t('brandFont.properties.version')}
                value={font.version ?? notRecorded}
                testId="font-detail-version"
              />
              {/* Wrapped, unlike the fields above: a copyright line and a licence are sentences, and
                  a sentence in the right-hand column of a two-column list sets one word per line. */}
              <Field
                wrap
                label={t('brandFont.properties.copyright')}
                value={font.copyright ?? notRecorded}
                testId="font-detail-copyright"
              />
              <Field
                wrap
                label={t('brandFont.properties.licenceTerms')}
                value={font.licenseDescription ?? notRecorded}
                testId="font-detail-licence"
              />
              {/* A link rather than a `Field`, because a licence url is there to be opened. Whatever
                  the file declared, which is why it is not trusted as same-origin. */}
              <div className="flex flex-col gap-1 text-sm">
                <dt className="text-muted-foreground">{t('brandFont.properties.licenceUrl')}</dt>
                <dd data-testid="font-detail-licence-url" className="break-words text-foreground">
                  {font.licenseUrl == null ? (
                    notRecorded
                  ) : (
                    <a
                      href={font.licenseUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="underline underline-offset-2"
                    >
                      {font.licenseUrl}
                    </a>
                  )}
                </dd>
              </div>
            </dl>
          </Section>
        </div>

        {/* The asset panel's pair, in the same place, plus replacing the file, which only a font
            needs. Outside the scrolling body so all three stay reachable however far down someone
            has read. */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border-light p-5">
          {canManageBrandAssets ? (
            <MoveToFolderMenu
              folders={folders}
              label={t('brandFont.moveFont')}
              currentFolderId={font.folderId}
              className="shrink-0"
              onMove={onMove}
            />
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button variant="outline" size="sm" className="shrink-0" disabled>
                    {t('brandFont.moveFont')}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{noPermission}</TooltipContent>
            </Tooltip>
          )}
          <div className="flex shrink-0 items-center gap-2">
            <input
              ref={replacePicker}
              type="file"
              accept={FONT_ACCEPT}
              className="hidden"
              data-testid="font-replace-input"
              onChange={(event) => {
                const file = event.target.files?.[0];
                // Cleared so choosing the same file again after a failure still raises a change.
                event.target.value = '';
                if (file !== undefined) onReplace(file);
              }}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isReplacing || !canManageBrandAssets}
                    onClick={() => replacePicker.current?.click()}
                  >
                    <FileUpIcon />
                    {isReplacing ? t('brandFont.replacingFont') : t('brandFont.replaceFontAction')}
                  </Button>
                </span>
              </TooltipTrigger>
              {!canManageBrandAssets && <TooltipContent>{noPermission}</TooltipContent>}
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!canManageBrandAssets}
                    onClick={onRequestDelete}
                  >
                    <Trash2Icon />
                    {t('brandFont.deleteFontAction')}
                  </Button>
                </span>
              </TooltipTrigger>
              {!canManageBrandAssets && <TooltipContent>{noPermission}</TooltipContent>}
            </Tooltip>
          </div>
        </div>
      </PanelShell>
    </TooltipProvider>
  );
};
